// Flag detail. Was a modal in the old UI; now a route so it's linkable and
// bookmarkable. Shows workspaces (name-resolved), disallowed workspaces,
// platforms, description, rollout state, and Features that claim this flag.
import { Link, useRoute } from "wouter";
import { useMemo, useState } from "react";
import type { FeatureFlag } from "@release-hub/shared";
import { useFeatures, useFlag, useWorkspaces } from "@/api/queries";
import { useCreateFeatureFromFlag } from "@/api/mutations";
import { useEffectiveRole } from "@/auth/role";
import { ApiError } from "@/api/client";
import { LoadingLine } from "@/components/LoadingLine";
import { flagRollout } from "@/lib/flagRollout";
import { RolloutBadge } from "@/lib/rolloutBadge";
import { Button } from "@/ui/button";
import { fmtDate } from "@/lib/format";
import { useLocation } from "wouter";

export function FlagDetail() {
  const [, params] = useRoute<{ id: string }>("/flags/:id");
  const id = params?.id ? decodeURIComponent(params.id) : undefined;
  const { data: flag, isPending, error } = useFlag(id);
  const { data: workspaces } = useWorkspaces();
  const { data: features } = useFeatures();
  const { isEditor } = useEffectiveRole();
  const create = useCreateFeatureFromFlag();
  const [, navigate] = useLocation();
  const [autodraftErr, setAutodraftErr] = useState<string>("");

  const wsById = useMemo(() => {
    const idx = new Map<string, string>();
    for (const w of workspaces ?? []) idx.set(w.id, w.name);
    return idx;
  }, [workspaces]);

  const claimed = useMemo(() => {
    if (!features || !flag) return [];
    return features.filter((f) => f.flag_ids.includes(flag.id));
  }, [features, flag]);

  if (isPending) return <LoadingLine />;
  if (error) {
    const msg =
      error instanceof ApiError && error.status === 404
        ? `Flag "${id}" not found — it may have been removed from squapi.`
        : `Failed to load: ${error instanceof Error ? error.message : "unknown"}`;
    return (
      <div className="space-y-4 py-6">
        <BackLink />
        <p className="text-destructive">{msg}</p>
      </div>
    );
  }
  if (!flag) return null;

  const autodraft = async () => {
    setAutodraftErr("");
    try {
      const { id: newId } = await create.mutateAsync(flag.id);
      navigate(`/features/${newId}`);
    } catch (e) {
      setAutodraftErr(
        e instanceof ApiError ? e.detail :
        e instanceof Error ? e.message : String(e),
      );
    }
  };

  // "*" is the wildcard — the flag is enabled for all workspaces.
  const resolveWs = (wsId: string) => {
    if (wsId === "*") return "All workspaces";
    return wsById.get(wsId) ?? wsId;
  };

  return (
    <div className="space-y-6 py-6">
      <BackLink />

      <header className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <code className="font-mono text-2xl text-text">{flag.id}</code>
          <RolloutBadge rollout={flagRollout(flag)} size="md" />
        </div>
        {flag.description && (
          <p className="text-text-secondary">{flag.description}</p>
        )}
      </header>

      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 rounded-lg border border-border bg-card p-5 md:grid-cols-[max-content_1fr]">
        <MetaRow
          label="Enabled workspaces"
          value={<WorkspaceList ids={flag.workspaces} resolve={resolveWs} />}
        />
        <MetaRow
          label="Disallowed workspaces"
          value={<WorkspaceList ids={flag.disallowedWorkspaces} resolve={resolveWs} />}
        />
        <MetaRow
          label="Platforms"
          value={
            flag.platforms.length === 0 ? null : (
              <div className="flex flex-wrap gap-1.5">
                {flag.platforms.map((p) => (
                  <span
                    key={p}
                    className="rounded-full bg-border px-2 py-0.5 font-mono text-[11px]"
                  >
                    {p}
                  </span>
                ))}
              </div>
            )
          }
        />
        <MetaRow
          label="Default enabled"
          value={flag.defaultEnabled ? "yes" : "no"}
        />
        <MetaRow
          label="Globally disabled"
          value={flag.globalIsDisabled ? "yes" : "no"}
        />
        <MetaRow
          label="Minimum app version"
          value={flag.minimumAppVersion}
        />
        <MetaRow
          label="Created"
          value={flag.timeCreated ? fmtDate(flag.timeCreated) : null}
        />
        <MetaRow
          label="Last updated"
          value={flag.lastUpdated ? fmtDate(flag.lastUpdated) : null}
        />
      </dl>

      <section>
        <h3 className="mb-3 font-serif text-lg">Claimed by feature launches</h3>
        {claimed.length === 0 ? (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-text-tertiary">
              No feature launches claim this flag yet.
            </p>
            {isEditor && (
              <Button size="sm" onClick={autodraft} disabled={create.isPending}>
                {create.isPending ? "Creating…" : "Autodraft feature launch"}
              </Button>
            )}
            {autodraftErr && (
              <span className="text-xs text-destructive">{autodraftErr}</span>
            )}
          </div>
        ) : (
          <ul className="space-y-2">
            {claimed.map((f) => (
              <li key={f.id}>
                <Link
                  href={`/features/${f.id}`}
                  className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 no-underline hover:border-border-hover"
                >
                  <span className="text-text">{f.name}</span>
                  {f.status === "draft" && (
                    <span className="rounded-full bg-border px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wider text-text-secondary">
                      Draft
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function BackLink() {
  return (
    <Link
      href="/timeline"
      className="inline-flex items-center gap-1 text-sm text-text-secondary no-underline hover:text-text"
    >
      ← Timeline
    </Link>
  );
}

function MetaRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <dt className="text-xs font-medium uppercase tracking-wider text-text-tertiary">
        {label}
      </dt>
      <dd className="text-sm text-text">
        {value == null || value === "" ? (
          <span className="text-text-tertiary">—</span>
        ) : (
          value
        )}
      </dd>
    </>
  );
}

function WorkspaceList({
  ids,
  resolve,
}: {
  ids: string[];
  resolve: (id: string) => string;
}) {
  if (ids.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {ids.map((id) => (
        <span
          key={id}
          className="inline-flex items-baseline gap-1 rounded-md bg-border/40 px-2 py-0.5 text-xs"
          title={id}
        >
          <span>{resolve(id)}</span>
          {resolve(id) !== id && (
            <span className="font-mono text-[10.5px] text-text-tertiary">
              {id.slice(0, 8)}
            </span>
          )}
        </span>
      ))}
    </div>
  );
}
